import { MoreHorizontal, PhoneIcon, Trash } from 'lucide-react'
import { useState } from 'react'

import { Button } from 'src/shared/components/ui/buttons/button'
import { Badge } from 'src/shared/components/ui/miscellaneous/badge'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from 'src/shared/components/ui/popups/alert-dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'src/shared/components/ui/popups/dropdown-menu'
import type { Invite } from 'src/shared/schemas/invites/invite'
import { cn } from 'src/shared/utils/cn'
import { formatTimeDelta } from 'src/shared/utils/dates/format-time-delta'

import { useDeleteInvite } from '../api/use-delete-invite'

interface InviteItemProps {
  invite: Invite
  onPhoneUpdate?: (invite: Invite) => void
  variant?: 'light' | 'dark'
}

export const InviteItem = ({ invite, onPhoneUpdate, variant = 'light' }: InviteItemProps) => {
  const timeAgo = formatTimeDelta(invite.timeSinceCreated, { short: false, future: false })
  const deleteInvite = useDeleteInvite()
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)

  const handleDelete = () => {
    deleteInvite.mutate(invite.id)
    setIsDeleteDialogOpen(false)
  }

  return (
    <div className="flex items-start justify-between py-2">
      <div className="flex flex-row gap-3">
        <div
          className={cn(
            'flex h-11 w-11 items-center justify-center rounded-full bg-[#f8f7f5]',
            variant === 'dark' && 'bg-[#3d4f49]',
          )}
        >
          <p className={cn('font-semibold uppercase tracking-wider', variant === 'dark' && 'text-white/65')}>
            {invite.invitedEmail?.slice(0, 2) ?? '??'}
          </p>
        </div>
        <div className="flex flex-col gap-1">
          <span className={cn('text-[15px] font-bold', variant === 'dark' && 'text-white')}>{invite.invitedEmail}</span>
          <div className="flex items-center gap-2">
            <Badge
              variant="accent"
              text={invite.invitedPhone ? invite.invitedPhone : 'Not provided'}
              size="mini"
              className={cn(
                'cursor-pointer py-0.5 text-[11px]',
                variant === 'dark' && 'border-none bg-muted text-white/65',
              )}
              icon={PhoneIcon}
              onClick={!invite.invitedPhone ? () => onPhoneUpdate?.(invite) : undefined}
            />
            <span className={cn('text-xs text-muted', variant === 'dark' && 'text-white/65')}>{timeAgo}</span>
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="size-5">
              <MoreHorizontal className="size-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
              <AlertDialogTrigger asChild>
                <DropdownMenuItem onSelect={(e: Event) => e.preventDefault()}>
                  <Trash className="mr-2 size-4" />
                  Delete
                </DropdownMenuItem>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete Invite</AlertDialogTitle>
                  <AlertDialogDescription>
                    Are you sure you want to delete this invite? This action cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handleDelete}>Delete</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  )
}
